import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, CheckCircle2, MessageCircle, Calendar, Phone, Sparkles,
  Building2, Users, ArrowRight, ShieldCheck, Clock, Zap, Check, Star
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTITUTE_TYPES = [
  { value: 'Coaching', label: 'Coaching / Institute' },
  { value: 'School', label: 'School (K-12)' },
  { value: 'College', label: 'College / Degree' },
  { value: 'Tuition', label: 'Tuition / Academy' },
];

const STUDENT_RANGES = [
  'Under 100',
  '100 – 300',
  '300 – 1,000',
  '1,000+ (Multi-Branch)',
];

const FEATURE_CHIPS = [
  'Biometric & WhatsApp Alerts',
  'Smart Fee Dues Recovery',
  'Own Branded Mobile App (Logo & Colors)',
  'Cloud Backup & Safe Restore',
  'AI Exam & OMR Checking',
  'Facebook Lead CRM',
  'LMS & Daily Practice Problems',
  'Multi-Branch Management',
  'Staff HRMS & Payroll',
  'Google 5-Star Reviews & QR',
];

export default function DemoEnquiryModal({ isOpen, onClose, initialRequirement = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    institute_name: '',
    city: '',
    institute_type: 'Coaching',
    student_count: '100 – 300',
    primary_requirement: initialRequirement || 'Biometric & WhatsApp Alerts',
    email: '',
    notes: '',
  });

  const [selectedFeatures, setSelectedFeatures] = useState(
    initialRequirement ? [initialRequirement] : ['Biometric & WhatsApp Alerts', 'Smart Fee Dues Recovery']
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leadInfo, setLeadInfo] = useState(null);

  // Close on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleFeature = (feat) => {
    setSelectedFeatures(prev =>
      prev.includes(feat) ? prev.filter(f => f !== feat) : [...prev, feat]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast.error('Please provide your name and WhatsApp number');
      return;
    }

    setSubmitting(true);
    const payload = {
      ...formData,
      primary_requirement: selectedFeatures.join(', ') || formData.primary_requirement,
    };

    try {
      await axios.post(`${API}/marketplace/demo-enquiry`, payload);
      setLeadInfo(payload);
      setSubmitted(true);
      toast.success('Demo request received! Our specialist will reach out.');
    } catch {
      // Still show success to not block user experience if network is flaky
      setLeadInfo(payload);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const getWhatsAppLink = () => {
    const phone = '916260129453';
    const text = encodeURIComponent(
      `Hello CoachHub Team! 👋\n\nI want to schedule a live demo for my institute:\n\n• *Name:* ${leadInfo?.name || formData.name}\n• *Institute:* ${leadInfo?.institute_name || formData.institute_name || 'N/A'}\n• *City:* ${leadInfo?.city || formData.city || 'N/A'}\n• *Students:* ${leadInfo?.student_count || formData.student_count}\n• *Key Requirements:* ${selectedFeatures.join(', ') || 'Complete ERP'}\n\nPlease share the demo time slot.`
    );
    return `https://wa.me/${phone}?text=${text}`;
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-brand-ink/80 backdrop-blur-md animate-in fade-in duration-200 cursor-pointer"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[92vh] flex flex-col rounded-3xl bg-white border border-slate-200 shadow-2xl overflow-hidden my-auto cursor-default"
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        {/* Compact Single-Heading Header */}
        <div className="bg-brand-navy px-5 py-4 sm:px-6 sm:py-4 text-white relative overflow-hidden flex-shrink-0 flex items-center justify-between border-b border-white/10">
          <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
          <div className="absolute -top-10 -right-10 w-36 h-36 bg-brand-gold/20 blur-2xl rounded-full pointer-events-none" />

          <h3 className="relative font-cabinet text-lg sm:text-xl font-black tracking-tight text-white pr-4">
            See CoachHub Live with Your Batches & Fees
          </h3>

          <button
            type="button"
            onClick={onClose}
            className="relative z-20 w-9 h-9 rounded-full bg-white/15 hover:bg-rose-500 text-white flex items-center justify-center transition-all cursor-pointer shadow-md hover:scale-105 active:scale-95 flex-shrink-0"
            aria-label="Close dialog"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 sm:p-7 overflow-y-auto flex-1">
          {submitted ? (
            <div className="text-center py-4 space-y-5">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center animate-bounce">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <div>
                <h4 className="font-cabinet text-2xl font-black text-brand-ink mb-1.5">
                  Demo Request Received, {leadInfo?.name}!
                </h4>
                <p className="text-sm text-slate-600 max-w-md mx-auto">
                  Our institute solution expert will contact you on WhatsApp at <strong className="text-brand-ink">{leadInfo?.phone}</strong> within 30 minutes to confirm your demo slot.
                </p>
              </div>

              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-left max-w-md mx-auto">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-800 mb-2">Demo Request Summary</p>
                <div className="space-y-1.5 text-xs sm:text-sm text-emerald-950">
                  <p><span className="text-emerald-700 font-medium">Institute:</span> {leadInfo?.institute_name || 'N/A'} ({leadInfo?.city || 'India'})</p>
                  <p><span className="text-emerald-700 font-medium">Student Strength:</span> {leadInfo?.student_count}</p>
                  <p><span className="text-emerald-700 font-medium">Key Focus:</span> {selectedFeatures.join(', ')}</p>
                </div>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-6 py-3.5 rounded-xl transition-all shadow-lg shadow-[#25D366]/30 text-sm"
                >
                  <MessageCircle className="w-5 h-5" /> Chat on WhatsApp Now
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-brand-ink font-semibold px-6 py-3.5 rounded-xl transition-colors text-sm"
                >
                  Close & Explore Website
                </button>
              </div>

              <div className="flex items-center justify-center gap-6 text-xs text-slate-500 pt-2 border-t border-slate-100">
                <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-emerald-600" /> 100% Confidential</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-brand-gold-hover" /> No credit card</span>
                <span className="flex items-center gap-1"><Zap className="w-4 h-4 text-blue-600" /> Free Setup Assist</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Your Name / Role <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rajesh Sharma (Director)"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    WhatsApp Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">+91</span>
                    <input
                      type="tel"
                      required
                      placeholder="98765 43210"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 pl-11 pr-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Institute / Coaching Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Apex Science Classes"
                    value={formData.institute_name}
                    onChange={e => setFormData({ ...formData, institute_name: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    City / Town
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Kota, Indore, Patna"
                    value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Institute Type
                  </label>
                  <select
                    value={formData.institute_type}
                    onChange={e => setFormData({ ...formData, institute_type: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-brand-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                  >
                    {INSTITUTE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Approx. Student Count
                  </label>
                  <select
                    value={formData.student_count}
                    onChange={e => setFormData({ ...formData, student_count: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-brand-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                  >
                    {STUDENT_RANGES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Modules selection */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Select Modules You Want to Explore (Click to toggle)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {FEATURE_CHIPS.map(feat => {
                    const active = selectedFeatures.includes(feat);
                    return (
                      <button
                        key={feat}
                        type="button"
                        onClick={() => toggleFeature(feat)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          active
                            ? 'bg-brand-ink text-brand-gold border border-brand-gold/30 shadow-sm'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'
                        }`}
                      >
                        {active && <Check className="w-3 h-3 text-brand-gold" />}
                        {feat}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Any specific question or setup challenge? (Optional)
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Need help integrating our ZKTeco biometric machine & migrating 450 students from Excel..."
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold resize-none"
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-6 py-3.5 rounded-xl border border-slate-200 text-slate-600 hover:text-brand-ink hover:bg-slate-100 font-semibold text-sm transition-colors text-center cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:flex-1 py-3.5 rounded-xl bg-brand-gold hover:bg-brand-gold-hover text-brand-ink font-cabinet font-black text-sm sm:text-base shadow-xl shadow-brand-gold/30 transition-all hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-brand-ink border-t-transparent rounded-full animate-spin" />
                      Booking Your Walkthrough...
                    </>
                  ) : (
                    <>
                      Book 20-Min Live Demo Now <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Free 14-day trial included</span>
                <span className="inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-brand-gold-hover" /> Free student data migration</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
